import Head from 'next/head';
import Link from 'next/link';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ExamplesSection from '../components/ExamplesSection';
import ValuePropositionSection from '../components/ValuePropositionSection';
import { SIGNUP_URL } from '../utils/constants';

export default function UseCases() {
  // Use case data for the page
  const useCases = [
    {
      title: "Design Agencies & Freelancers",
      description: "Scale your design services without increasing work hours",
      features: [
        "Create templates once, generate countless variations",
        "Deliver consistent branded materials across campaigns",
        "No IT infrastructure to manage — we handle the heavy lifting",
        "Scale from one asset to thousands on-demand",
        "Create new revenue streams with personalized template services"
      ],
      image: "/images/use-cases/agency.jpg",
      ctaLink: SIGNUP_URL,
      ctaText: "Start Your Agency Trial",
      testimonial: {
        quote: "Personalia has transformed our design workflow. We now manage campaigns with hundreds of personalized assets that would have been impossible before.",
        author: "Creative Director, Design Agency",
        company: "Brand Forward"
      }
    },
    {
      title: "Marketing Teams",
      description: "Personalize marketing materials at scale without design bottlenecks",
      features: [
        "Localize marketing materials for multiple regions",
        "Create personalized customer communications",
        "Generate on-brand social media content quickly",
        "Customize materials based on customer segments",
        "Streamline approval processes with consistent templates"
      ],
      image: "/images/use-cases/marketing.jpg",
      ctaLink: SIGNUP_URL,
      ctaText: "Explore Marketing Solutions",
      testimonial: {
        quote: "We reduced our design request time from days to minutes and increased our campaign output by 300% using Personalia's automation.",
        author: "Marketing Operations Manager",
        company: "Global Retail Brand"
      }
    },
    {
      title: "Software Developers",
      description: "Add personalized asset generation to your applications",
      features: [
        "Integrate professional design capabilities via API",
        "No rendering servers to build or maintain",
        "Scale to any volume — our infrastructure handles the load",
        "Create white-label document generation services",
        "Build personalized customer portals with dynamic content"
      ],
      image: "/images/use-cases/developer.jpg",
      ctaLink: "/integrations",
      ctaText: "Explore API Documentation",
      testimonial: {
        quote: "Implementing the Personalia API saved us months of development time. Our customers can now generate beautiful PDFs directly from our platform.",
        author: "CTO",
        company: "SaaS Platform"
      }
    }
  ];

  return (
    <>
      <Head>
        <title>Use Cases | Personalia - InDesign Automation Platform</title>
        <meta name="description" content="Discover how design agencies, marketing teams, and developers use Personalia to automate and scale personalized asset creation." />
      </Head>

      <Header />

      <main id="main-content">
        {/* Hero Section */}
        <section className="bg-gray-900 text-white py-16 md:py-24">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Personalize at Scale with InDesign Automation
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                See how different teams and industries leverage Personalia to transform their creative workflows. We handle the IT infrastructure and scalability — you focus on what matters.
              </p>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="section">
          <div className="container-custom">
            <div className="space-y-24">
              {useCases.map((useCase, index) => (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 !== 0 ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={index % 2 !== 0 ? 'lg:col-start-2' : ''}>
                    <h2 className="text-3xl font-bold mb-4">{useCase.title}</h2>
                    <p className="text-lg text-gray-600 mb-6">{useCase.description}</p>
                    
                    <h3 className="text-xl font-semibold mb-3">Key Benefits</h3>
                    <ul className="space-y-2 mb-8">
                      {useCase.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mt-1 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-100 italic">
                      <p className="text-gray-700 mb-4">"{useCase.testimonial.quote}"</p>
                      <p className="text-sm font-medium">
                        {useCase.testimonial.author}, <span className="text-primary">{useCase.testimonial.company}</span>
                      </p>
                    </div>
                    
                    {useCase.ctaLink.startsWith('http') ? (
                      <a
                        href={useCase.ctaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                      >
                        {useCase.ctaText}
                      </a>
                    ) : (
                      <Link href={useCase.ctaLink} className="btn btn-primary">
                        {useCase.ctaText}
                      </Link>
                    )}
                  </div>
                  
                  <div className={`bg-gray-100 rounded-lg overflow-hidden h-80 ${index % 2 !== 0 ? 'lg:col-start-1' : ''}`}>
                    {/* Placeholder for use case images */}
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center p-8">
                        <div className="inline-block rounded-full bg-primary bg-opacity-10 p-4 mb-4">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            {index === 0 ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            ) : index === 1 ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M4 12l4-4 4 4-4 4" />
                            )}
                          </svg>
                        </div>
                        <p className="text-gray-500">{useCase.title} image placeholder</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ExamplesSection />
        <ValuePropositionSection />

        {/* Retention Benefits Section */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Creative Automation Benefits</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Personalia helps agencies and creative teams focus on what matters most: creating great designs. We handle the infrastructure, so you can scale from one asset to hundreds of thousands.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-md p-8">
                <div className="inline-block p-3 bg-primary bg-opacity-10 rounded-full mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4">Reclaim Creative Time</h3>
                <p className="text-gray-600">
                  Free your designers from repetitive production tasks so they can focus on creative work. 
                  Automated asset generation means they can dedicate time to innovation rather than versioning.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-8">
                <div className="inline-block p-3 bg-primary bg-opacity-10 rounded-full mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4">Maintain Brand Consistency</h3>
                <p className="text-gray-600">
                  Ensure perfect brand compliance across all outputs with locked templates. 
                  No more off-brand materials or inconsistent designs across channels or regions.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-8">
                <div className="inline-block p-3 bg-primary bg-opacity-10 rounded-full mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4">Increase Profitability</h3>
                <p className="text-gray-600">
                  Handle more client work without expanding your team. Personalia helps agencies 
                  service more accounts and offer new personalization services that clients value.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-8">
                <div className="inline-block p-3 bg-primary bg-opacity-10 rounded-full mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4">Empower Client Self-Service</h3>
                <p className="text-gray-600">
                  Give clients the ability to generate their own materials within brand guidelines. 
                  Create new revenue streams through template subscription services.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section bg-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your Creative Workflow?</h2>
            <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
              See how Personalia can work for your specific use case. Get in touch with our team to learn more.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="/contact" className="btn bg-white text-primary hover:bg-opacity-90">
                Talk to an Expert
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
